import { useState, useEffect } from "react";
import Pagination from "../../Common/Pagination";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScholarshipForm } from "./ScholarshipForm";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { API_ENDPOINTS } from "../../../constants/config"

function ScholarshipContent() {
  const [scholarships, setScholarships] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedScholarship, setSelectedScholarship] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [editingScholarship, setEditingScholarship] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignedScholarships, setAssignedScholarships] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [schPage, setSchPage] = useState(1);
  const [schLimit, setSchLimit] = useState(10);
  const [assignedPage, setAssignedPage] = useState(1);
  const [assignedLimit, setAssignedLimit] = useState(10);

  const getSchoolId = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const s = user?.school;
    if (!s) return null;
    return typeof s === "object" && s._id != null ? s._id : s;
  };

  useEffect(() => {
    fetchScholarships();
    fetchStudents();
    fetchAcademicYears();
    fetchAssignedScholarships();
    // eslint-disable-next-line
  }, []);

  const fetchScholarships = async () => {
    try {
      const schoolId = getSchoolId();
      const response = await fetch(API_ENDPOINTS.scholarshipsBySchool(schoolId), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch scholarships');
      }

      const data = await response.json();

      // Normalize scholarships to always be an array. API may return an object wrapper.
      let scholarshipsPayload = [];
      if (Array.isArray(data)) {
        scholarshipsPayload = data;
      } else if (Array.isArray(data.data)) {
        scholarshipsPayload = data.data;
      } else if (Array.isArray(data.scholarships)) {
        scholarshipsPayload = data.scholarships;
      } else if (Array.isArray(data.data?.scholarships)) {
        scholarshipsPayload = data.data.scholarships;
      } else {
        // unexpected shape — log for debugging
        // eslint-disable-next-line no-console
        console.warn("fetchScholarships: unexpected payload shape", data);
      }

      setScholarships(scholarshipsPayload);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch scholarships');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.usersStudents, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }

      const data = await response.json();
      setStudents(Array.isArray(data.data.students) ? data.data.students : []);
    } catch (err) {
      setError(err.message);
      setStudents([]);
      toast.error('Failed to fetch students');
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.idCardsAcademicYears, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch academic years');
      }

      const data = await response.json();
      setAcademicYears(data.data);

      const currentYear = data.data.find((year) => year.isCurrent);
      if (currentYear) {
        setSelectedAcademicYear(currentYear._id);
      } else if (data.data.length > 0) {
        setSelectedAcademicYear(data.data[0]._id);
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch academic years');
    }
  };

  const fetchAssignedScholarships = async () => {
    try {
      const schoolId = getSchoolId();
      const response = await fetch(API_ENDPOINTS.scholarshipsAssigned(schoolId), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assigned scholarships');
      }

      const data = await response.json();
      setAssignedScholarships(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch assigned scholarships');
    }
  };

  const deleteScholarship = async (id) => {
    try {
      const schoolId = getSchoolId();
      const response = await fetch(API_ENDPOINTS.scholarship(id), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          'Content-Type': 'application/json',
        },
        credentials: "include",
        body: JSON.stringify({ schoolId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete scholarship');
      }

      toast.success('Scholarship deleted successfully');
      fetchScholarships();
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Failed to delete scholarship');
    }
  };

  const assignScholarship = async () => {
    try {
      const schoolId = getSchoolId();
      const selectedScholarshipObj = scholarships.find(s => s._id === selectedScholarship);
      
      // Only include applicableMonths if scholarship is Monthly
      const requestBody = {
        userId: selectedStudent,
        scholarshipId: selectedScholarship,
        academicYear: selectedAcademicYear,
        schoolId
      };
      
      if (selectedScholarshipObj?.frequency === "Monthly" && selectedMonths.length > 0) {
        requestBody.applicableMonths = selectedMonths;
      }

      const response = await fetch(API_ENDPOINTS.scholarshipsAssign, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to assign scholarship');
      }

      toast.success('Scholarship assigned successfully');
      setSelectedStudent("");
      setSelectedScholarship("");
      setSelectedMonths([]);
      fetchAssignedScholarships();
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Failed to assign scholarship');
    }
  };

  const handleMonthToggle = (month) => {
    setSelectedMonths(prev => 
      prev.includes(month) 
        ? prev.filter(m => m !== month)
        : [...prev, month].sort((a, b) => a - b)
    );
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const selectedScholarshipObj = scholarships.find(s => s._id === selectedScholarship);
  const isMonthlyScholarship = selectedScholarshipObj?.frequency === "Monthly";

  const editScholarship = (scholarship) => {
    setEditingScholarship(scholarship);
  };

  const handleScholarshipFormSuccess = () => {
    fetchScholarships();
    setEditingScholarship(null);
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-64 text-xl font-semibold text-[#0263cc]">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="flex justify-center items-center h-64 text-red-600 text-lg">
        {error}
      </div>
    );

  return (
    <div className="bg-white p-8 rounded-2xl shadow-lg mx-auto">
      <h2 className="text-2xl font-bold text-[#0263cc] mb-6 tracking-tight flex items-center gap-2">
        <span className="inline-block w-2 h-6 bg-[#0263cc] rounded-full mr-2"></span>
        Scholarship Management
      </h2>

      {/* Scholarships Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
        <Table>
          <TableHeader className="bg-[#0263cc]/10">
            <TableRow>
              <TableHead className="text-[#0263cc] font-semibold">Scholarship Name</TableHead>
              <TableHead className="text-[#0263cc] font-semibold">Amount</TableHead>
              <TableHead className="text-[#0263cc] font-semibold">Type</TableHead>
              <TableHead className="text-[#0263cc] font-semibold">Frequency</TableHead>
              <TableHead className="text-[#0263cc] font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scholarships.slice((schPage - 1) * schLimit, schPage * schLimit).map((scholarship) => (
              <TableRow key={scholarship._id} className="hover:bg-[#0263cc]/5 transition">
                <TableCell className="font-medium">{scholarship.name}</TableCell>
                <TableCell>₹{scholarship.amount}</TableCell>
                <TableCell>{scholarship.type}</TableCell>
                <TableCell>{scholarship.frequency}</TableCell>
                <TableCell className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-[#0263cc]/20"
                    onClick={() => editScholarship(scholarship)}
                  >
                    <Pencil className="h-4 w-4 text-[#0263cc]" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="hover:bg-red-600/80"
                    onClick={() => deleteScholarship(scholarship._id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {scholarships.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-gray-400 py-6">
                  No scholarships defined
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination
          currentPage={schPage}
          totalItems={scholarships.length}
          itemsPerPage={schLimit}
          onPageChange={setSchPage}
          onItemsPerPageChange={(newLimit) => { setSchLimit(newLimit); setSchPage(1); }}
          itemLabel="scholarships"
        />
      </div>

      {/* Scholarship Form */}
      <ScholarshipForm
        editingScholarship={editingScholarship}
        onSuccess={handleScholarshipFormSuccess}
      />

      {/* Assigned Scholarships */}
      <div className="pt-4">
        <h3 className="text-xl font-semibold text-[#0263cc] mb-4">Assigned Scholarships</h3>
        <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
          <Table>
            <TableHeader className="bg-[#0263cc]/10">
              <TableRow>
                <TableHead className="text-[#0263cc] font-semibold">Student Name</TableHead>
                <TableHead className="text-[#0263cc] font-semibold">Scholarship</TableHead>
                <TableHead className="text-[#0263cc] font-semibold">Amount</TableHead>
                <TableHead className="text-[#0263cc] font-semibold">Applicable Months</TableHead>
                <TableHead className="text-[#0263cc] font-semibold">Academic Year</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignedScholarships.slice((assignedPage - 1) * assignedLimit, assignedPage * assignedLimit).map((assignment) => {
                const monthNames = [
                  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                ];
                const applicableMonths = assignment.applicableMonths || [];
                const monthsDisplay = applicableMonths.length > 0
                  ? applicableMonths.map(m => monthNames[m - 1]).join(", ")
                  : "All months";
                
                return (
                  <TableRow key={assignment._id} className="hover:bg-[#0263cc]/5 transition">
                    <TableCell>
                      {assignment.user ? `${assignment.user.firstName} ${assignment.user.lastName}` : "Unknown"}
                    </TableCell>
                    <TableCell>{assignment.scholarship?.name || "Unknown"}</TableCell>
                    <TableCell>₹{assignment.scholarship?.amount || "N/A"}</TableCell>
                    <TableCell className="text-sm">
                      {assignment.scholarship?.frequency === "Monthly" ? monthsDisplay : "-"}
                    </TableCell>
                    <TableCell>
                      {assignment.academicYear
                        ? `${assignment.academicYear.startYear} - ${assignment.academicYear.endYear}`
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                );
              })}
              {assignedScholarships.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-400 py-6">
                    No scholarships assigned
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination
            currentPage={assignedPage}
            totalItems={assignedScholarships.length}
            itemsPerPage={assignedLimit}
            onPageChange={setAssignedPage}
            onItemsPerPageChange={(newLimit) => { setAssignedLimit(newLimit); setAssignedPage(1); }}
            itemLabel="assignments"
          />
        </div>
      </div>

      {/* Assign Scholarship */}
      <div className="pt-4">
        <h3 className="text-xl font-semibold text-[#0263cc] mb-4">Assign Scholarship</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0263cc] bg-gray-50"
              >
                <option value="">Select Student</option>
                {Array.isArray(students) && students.map((student) => (
                  <option key={student._id} value={student._id}>
                    {student.firstName} {student.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scholarship</label>
              <select
                value={selectedScholarship}
                onChange={(e) => {
                  setSelectedScholarship(e.target.value);
                  setSelectedMonths([]); // Reset months when scholarship changes
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0263cc] bg-gray-50"
              >
                <option value="">Select Scholarship</option>
                {scholarships.map((scholarship) => (
                  <option key={scholarship._id} value={scholarship._id}>
                    {scholarship.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
              <select
                value={selectedAcademicYear}
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0263cc] bg-gray-50"
              >
                <option value="">Select Academic Year</option>
                {academicYears.map((year) => (
                  <option key={year._id} value={year._id}>
                    {year.startYear} - {year.endYear} {year.isCurrent ? "(Current)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={assignScholarship}
                disabled={!selectedStudent || !selectedScholarship || !selectedAcademicYear}
                className="w-full bg-[#0263cc] hover:bg-[#024b99] text-white font-semibold rounded-lg px-4 py-2 transition"
              >
                Assign Scholarship
              </Button>
            </div>
          </div>
          
          {/* Months Selector for Monthly Scholarships */}
          {isMonthlyScholarship && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Applicable Months <span className="text-xs text-gray-500">(Optional - leave empty for all months)</span>
              </label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {monthNames.map((month, index) => {
                  const monthNumber = index + 1;
                  const isSelected = selectedMonths.includes(monthNumber);
                  return (
                    <button
                      key={monthNumber}
                      type="button"
                      onClick={() => handleMonthToggle(monthNumber)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                        isSelected
                          ? 'bg-[#0263cc] text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-[#0263cc]'
                      }`}
                    >
                      {month}
                    </button>
                  );
                })}
              </div>
              {selectedMonths.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {selectedMonths.map(m => monthNames[m - 1]).join(", ")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScholarshipContent;
